import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Package, Plane, Wallet, Award, LogOut, Menu, X } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, fetchUser } = useContext(AuthContext);
  const [shipments, setShipments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [shipmentsRes, bookingsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipping/my-shipments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/travel/bookings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (shipmentsRes.ok) {
        const shipmentsData = await shipmentsRes.json();
        setShipments(shipmentsData.shipments || []);
      }

      if (bookingsRes.ok) {
        const bookingsData = await bookingsRes.json();
        setBookings(bookingsData.bookings || []);
      }
      
      await fetchUser();
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="dashboard">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-[#18181B] border border-white/10 p-3 text-white"
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X /> : <Menu />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#18181B] border-r border-white/10 transition-transform duration-300 z-40 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`} data-testid="sidebar">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="ZN Networks" className="h-10 w-auto" />
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Sora' }}>Networks</h1>
          </div>

          <nav className="space-y-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full flex items-center gap-3 px-4 py-3 text-white bg-white/5 border-l-2 border-[#FFD60A] hover:bg-white/10 transition-colors"
              data-testid="nav-dashboard"
            >
              <Wallet className="w-5 h-5" />
              <span style={{ fontFamily: 'Manrope' }}>Dashboard</span>
            </button>
            <button
              onClick={() => navigate('/travel')}
              className="w-full flex items-center gap-3 px-4 py-3 text-white/60 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="nav-travel"
            >
              <Plane className="w-5 h-5" />
              <span style={{ fontFamily: 'Manrope' }}>Travel</span>
            </button>
            <button
              onClick={() => navigate('/shipping')}
              className="w-full flex items-center gap-3 px-4 py-3 text-white/60 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="nav-shipping"
            >
              <Package className="w-5 h-5" />
              <span style={{ fontFamily: 'Manrope' }}>Shipping</span>
            </button>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-white/60 hover:text-[#FF3B30] hover:bg-white/5 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span style={{ fontFamily: 'Manrope' }}>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-8">
        {/* Top Bar */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Welcome back, {user?.full_name}!</h2>
          <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Here's your account overview</p>
        </div>

        {/* Wallet Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card-glass p-6 border-l-4 border-[#FFD60A]" data-testid="wallet-usd">
            <div className="flex items-center justify-between mb-2">
              <Wallet className="w-5 h-5 text-[#FFD60A]" />
              <span className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>USD Wallet</span>
            </div>
            <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Sora' }}>${user?.wallet_usd?.toFixed(2) || '0.00'}</div>
          </div>

          <div className="card-glass p-6 border-l-4 border-[#10B981]" data-testid="wallet-ngn">
            <div className="flex items-center justify-between mb-2">
              <Wallet className="w-5 h-5 text-[#10B981]" />
              <span className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>NGN Wallet</span>
            </div>
            <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Sora' }}>₦{user?.wallet_ngn?.toFixed(2) || '0.00'}</div>
          </div>

          <div className="card-glass p-6 border-l-4 border-[#FF3B30]" data-testid="loyalty-points">
            <div className="flex items-center justify-between mb-2">
              <Award className="w-5 h-5 text-[#FF3B30]" />
              <span className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>Loyalty Points</span>
            </div>
            <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{user?.loyalty_points || 0}</div>
          </div>

          <div className="card-glass p-6 border-l-4 border-[#3B82F6]" data-testid="referral-code">
            <div className="flex items-center justify-between mb-2">
              <Award className="w-5 h-5 text-[#3B82F6]" />
              <span className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>Referral Code</span>
            </div>
            <div className="text-2xl font-bold text-white font-mono" style={{ fontFamily: 'JetBrains Mono' }}>{user?.referral_code}</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <button
            onClick={() => navigate('/travel')}
            className="card-glass p-8 hover:border-[#FFD60A]/50 transition-all duration-300 text-left group"
            data-testid="quick-travel-btn"
          >
            <Plane className="w-12 h-12 text-[#FFD60A] mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
            <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Book Travel</h3>
            <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Search flights and hotels</p>
          </button>

          <button
            onClick={() => navigate('/shipping')}
            className="card-glass p-8 hover:border-[#FF3B30]/50 transition-all duration-300 text-left group"
            data-testid="quick-shipping-btn"
          >
            <Package className="w-12 h-12 text-[#FF3B30] mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
            <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Ship Package</h3>
            <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Calculate and book shipping</p>
          </button>
        </div>

        {/* Active Shipments */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Sora' }}>Active Shipments</h3>
          {shipments.length > 0 ? (
            <div className="space-y-4">
              {shipments.slice(0, 3).map((shipment) => (
                <div key={shipment.id} className="card-glass p-6" data-testid={`shipment-${shipment.tracking_number}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#FFD60A] font-mono font-bold" style={{ fontFamily: 'JetBrains Mono' }}>{shipment.tracking_number}</span>
                        <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                          shipment.status === 'confirmed' ? 'bg-[#10B981]/20 text-[#10B981]' :
                          shipment.status === 'pending_payment' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                          'bg-white/10 text-white/60'
                        }`}>{shipment.status.replace('_', ' ')}</span>
                      </div>
                      <p className="text-white/60 text-sm" style={{ fontFamily: 'Manrope' }}>
                        {shipment.from_location} → {shipment.to_location}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora' }}>${shipment.cost.toFixed(2)}</div>
                      <p className="text-white/40 text-sm" style={{ fontFamily: 'Manrope' }}>{shipment.final_weight.toFixed(2)} lbs</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-glass p-12 text-center">
              <Package className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/40" style={{ fontFamily: 'Manrope' }}>No active shipments</p>
            </div>
          )}
        </div>

        {/* Travel Bookings */}
        <div>
          <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Sora' }}>Travel Bookings</h3>
          {bookings.length > 0 ? (
            <div className="space-y-4">
              {bookings.slice(0, 3).map((booking) => (
                <div key={booking.id} className="card-glass p-6" data-testid={`booking-${booking.booking_reference}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#FFD60A] font-mono font-bold" style={{ fontFamily: 'JetBrains Mono' }}>{booking.booking_reference}</span>
                        <span className="px-3 py-1 text-xs uppercase tracking-wider bg-[#10B981]/20 text-[#10B981]">{booking.status}</span>
                      </div>
                      <p className="text-white/60 text-sm capitalize" style={{ fontFamily: 'Manrope' }}>{booking.booking_type}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora' }}>${booking.amount.toFixed(2)}</div>
                      <p className="text-white/40 text-sm uppercase" style={{ fontFamily: 'Manrope' }}>{booking.currency}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-glass p-12 text-center">
              <Plane className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/40" style={{ fontFamily: 'Manrope' }}>No travel bookings</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;